/**
 * Eingebettete Kundenakte im Adressbuch-Kontakt-Detail (P4.3, 2026-07-06).
 *
 * Abgelöst (Dokument-Navigation 1.45, 2026-09-30): Die Standard-Komponente
 * CustomerAkteEmbeddedPanel wird nirgends mehr eingebunden — die Kontakt-Akte
 * (AdressbuchContactAkte) rendert ihre Reiter selbst. Nach Entscheid E9 bleibt sie
 * stehen und wird nicht umgebaut. Lebendig ist nur noch CustomerProjectsTab (unten).
 *
 * Nachbau des Mockups: 4-KPI-Reihe + Sub-Tab-Bar (Aufgaben · Berichte · Akte
 * · Projekte). Ersetzt die alte Standalone-Route /kunden/:id, die nur noch
 * als Redirect existiert.
 *
 * Nutzt dieselben Public-API-Komponenten wie die Legacy-Seite:
 *  - CustomerSummary       (stammdaten)
 *  - CustomerTasksList     (bauberichteV2)
 *  - CustomerReportsList   (bauberichteV2)
 *  - CustomerAkte          (bibliothek)
 * Datenquelle: /api/portal/customers/:id/overview
 */
import { useEffect, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, FolderKanban, ListTodo, FileText, FolderTree, LayoutGrid } from 'lucide-react';
import { api, apiFetch } from '@onvice/portal-shell-api';
import { GuardedModal, useDialogs } from '@onvice/portal-ui';
import { useFederatedModule } from '../lib/useFederatedModule';

// Alle drei Cross-Modul-Komponenten (stammdaten/bauberichteV2/bibliothek)
// dynamisch nachladen. Statische Imports wuerden den Federation-Bootstrap
// zwingen alle Remotes preload zu fetchen, was den Portal-Boot bei einzelnen
// Container-Ausfaellen kippt. Regel: [[feedback_federation_no_static_imports]].
type CustomerSummaryData = Record<string, unknown> & { id: string };
type CustomerTaskRow = Record<string, unknown> & { id: string };
type CustomerReportRow = Record<string, unknown> & { id: string };
type CustomerAkteHeader = Record<string, unknown>;
type CustomerAkteDocument = Record<string, unknown> & { id: string };
type StammdatenPublic = {
  CustomerSummary: ComponentType<{ customer: CustomerSummaryData }>;
};
type BauberichteV2Public = {
  CustomerTasksList: ComponentType<{ tasks: CustomerTaskRow[] }>;
  CustomerReportsList: ComponentType<{ reports: CustomerReportRow[] }>;
};
type BibliothekPublic = {
  CustomerAkte: ComponentType<{ akte: CustomerAkteHeader | null; documents: CustomerAkteDocument[] }>;
};

interface OverviewResponse {
  customer: CustomerSummaryData;
  counts: { tasks: number; reports: number; akten_docs: number; projects?: number };
  visible_tabs: string[];
  tasks: CustomerTaskRow[];
  reports: CustomerReportRow[];
  akte: CustomerAkteHeader | null;
  documents: CustomerAkteDocument[];
}

type TabKey = 'stammdaten' | 'tasks' | 'reports' | 'akten' | 'projekte';

const TAB_LABEL: Record<TabKey, string> = {
  stammdaten: 'Stammdaten',
  tasks: 'Aufgaben',
  reports: 'Berichte',
  akten: 'Akte',
  projekte: 'Projekte',
};

interface Props {
  /** Bevorzugt — nutzt /portal/contacts/:id/overview (W5b, 2026-07-06). */
  contactId?: string;
  /** Fallback fuer Legacy-Aufrufer — nutzt /portal/customers/:id/overview. */
  customerId?: string;
  /** Anfangs-Tab; Default 'stammdaten'. */
  initialTab?: TabKey;
}

export default function CustomerAkteEmbeddedPanel({ contactId, customerId, initialTab = 'stammdaten' }: Props) {
  const endpoint = contactId
    ? `/portal/contacts/${contactId}/overview`
    : `/portal/customers/${customerId}/overview`;
  const cacheKey = contactId ?? customerId ?? '';
  const stammdaten = useFederatedModule<StammdatenPublic>('stammdaten/public');
  const bv2 = useFederatedModule<BauberichteV2Public>('bauberichteV2/public');
  const bibliothek = useFederatedModule<BibliothekPublic>('bibliothek/public');
  const CustomerSummary = stammdaten?.CustomerSummary ?? null;
  const CustomerTasksList = bv2?.CustomerTasksList ?? null;
  const CustomerReportsList = bv2?.CustomerReportsList ?? null;
  const CustomerAkte = bibliothek?.CustomerAkte ?? null;
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    setError(null);
    api.get<{ success: boolean; data: OverviewResponse }>(endpoint)
      .then((res) => {
        if (aborted) return;
        if (res.success) setOverview(res.data);
        else setError('Kunde konnte nicht geladen werden');
      })
      .catch((err: unknown) => {
        if (aborted) return;
        const status = (err as { status?: number }).status;
        if (status === 404) setError('Kunde nicht gefunden');
        else if (status === 403) setError('Kein Zugriff auf diesen Kunden');
        else setError('Fehler beim Laden');
      })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error || !overview) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
        {error ?? 'Unbekannter Fehler'}
      </div>
    );
  }

  const { customer, counts, visible_tabs } = overview;
  const tabs = visible_tabs as TabKey[];

  return (
    <div className="space-y-4">
      {/* 4-KPI-Reihe */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi icon={ListTodo}    label="Offene Aufgaben" value={counts.tasks} />
        <Kpi icon={FileText}    label="Berichte"        value={counts.reports} />
        <Kpi icon={FolderTree}  label="Akten-Dokumente" value={counts.akten_docs} />
        <Kpi icon={LayoutGrid}  label="Projekte"        value={counts.projects ?? 0} />
      </div>

      {/* Sub-Tab-Bar */}
      <nav className="border-b border-default">
        <ul className="flex flex-wrap gap-1 -mb-px">
          {tabs.map((tab) => {
            const isActive = tab === activeTab;
            const count = tab === 'tasks' ? counts.tasks
                       : tab === 'reports' ? counts.reports
                       : tab === 'akten' ? counts.akten_docs
                       : tab === 'projekte' ? (counts.projects ?? null)
                       : null;
            return (
              <li key={tab}>
                <button
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={
                    'block px-3 py-2 text-xs border-b-2 transition-colors ' +
                    (isActive
                      ? 'border-primary text-primary font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-default')
                  }
                  aria-current={isActive ? 'page' : undefined}
                >
                  {TAB_LABEL[tab]}
                  {count !== null ? <span className="ml-1.5 text-[10px] text-muted-foreground">({count})</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Content */}
      <section>
        {activeTab === 'stammdaten' && (CustomerSummary ? <CustomerSummary customer={customer} /> : <ModulOffline modul="Stammdaten" />)}
        {activeTab === 'tasks'      && (CustomerTasksList ? <CustomerTasksList tasks={overview.tasks} /> : <ModulOffline modul="Bauberichte" />)}
        {activeTab === 'reports'    && (CustomerReportsList ? <CustomerReportsList reports={overview.reports} /> : <ModulOffline modul="Bauberichte" />)}
        {activeTab === 'akten'      && (CustomerAkte ? <CustomerAkte akte={overview.akte} documents={overview.documents} /> : <ModulOffline modul="Bibliothek" />)}
        {activeTab === 'projekte'   && <CustomerProjectsTab customerId={customer.id} />}
      </section>
    </div>
  );
}

function ModulOffline({ modul }: { modul: string }) {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
      Modul <b>{modul}</b> ist gerade nicht erreichbar. Sobald es antwortet, erscheint dieser Inhalt automatisch.
    </div>
  );
}

function Kpi({ icon: Icon, label, value }: { icon: React.ComponentType<{ size?: number; className?: string }>; label: string; value: number }) {
  return (
    <div className="rounded-lg border border-default bg-card/60 px-3 py-2 flex items-center gap-3">
      <Icon size={18} className="text-brand-400 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-lg font-semibold text-foreground leading-none">{value}</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{label}</div>
      </div>
    </div>
  );
}

interface ProjectRow {
  id: string;
  name: string;
  status: 'offen' | 'in_arbeit' | 'abgeschlossen' | 'abgerechnet';
  startDate: string | null;
  endDate: string | null;
  parentName: string | null;
  taskCount?: number;
  childCount?: number;
  archivedAt: string | null;
}

const PROJECT_STATUS_LABEL: Record<ProjectRow['status'], string> = {
  offen: 'Offen',
  in_arbeit: 'In Arbeit',
  abgeschlossen: 'Abgeschlossen',
  abgerechnet: 'Abgerechnet',
};

/**
 * Projekte-Reiter der Kontakt-Akte. Dokument-Navigation 1.45 (2026-09-30): Ladefehler
 * sichtbar, Neuanlage im geschützten Dialog (Escape/Klick daneben fragen bei Eingabe
 * nach), Anlegefehler als Meldung statt Konsole.
 */
export function CustomerProjectsTab({ customerId }: { customerId: string }) {
  const navigate = useNavigate();
  const dlg = useDialogs();
  const [items, setItems] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingBusy, setCreatingBusy] = useState(false);
  const [newName, setNewName] = useState('');

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const qs = showArchived ? '&includeArchived=true' : '';
      const r = await apiFetch<{ success: boolean; data: ProjectRow[] }>(
        `/modules/bauberichteV2/projects?customerId=${customerId}${qs}`,
      );
      setItems(r.data ?? []);
    } catch (e: unknown) {
      setItems([]);
      setLoadError(`Projekte nicht geladen: ${(e as Error).message || 'Verbindung fehlgeschlagen'}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [customerId, showArchived]);

  async function createProject() {
    if (!newName.trim() || creatingBusy) return;
    setCreatingBusy(true);
    try {
      await apiFetch('/modules/bauberichteV2/projects', {
        method: 'POST',
        body: JSON.stringify({ customerId, name: newName.trim(), status: 'offen' }),
      });
      setNewName('');
      setCreating(false);
      void load();
    } catch (e) {
      await dlg.error('Projekt nicht angelegt', e);
    } finally {
      setCreatingBusy(false);
    }
  }

  return (
    <div className="space-y-3" data-test-anchor="kunde-projekte">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground inline-flex items-center gap-2">
          <FolderKanban size={16} /> Projekte ({items.filter((p) => !p.archivedAt).length})
        </h3>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-primary" />
            Archiv
          </label>
          <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-xs min-h-[36px]">
            <Plus size={12} /> Neu
          </button>
        </div>
      </div>

      <GuardedModal
        open={creating}
        title="Neues Projekt"
        label="Neues Projekt"
        dirty={newName.trim() !== ''}
        busy={creatingBusy}
        onClose={() => { setCreating(false); setNewName(''); }}
        size="sm"
        testAnchor="projekt-neu"
        footer={(requestClose) => (
          <>
            <button type="button" onClick={requestClose} disabled={creatingBusy} className="px-3 py-2 rounded border border-slate-700 text-slate-300 text-sm min-h-[44px]">Abbrechen</button>
            <button type="button" onClick={() => void createProject()} disabled={!newName.trim() || creatingBusy} className="px-3 py-2 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm disabled:opacity-50 min-h-[44px]">
              {creatingBusy ? 'Legt an…' : 'Anlegen'}
            </button>
          </>
        )}
      >
        <label className="block text-sm">
          <span className="text-slate-400 text-[12px]">Projektname</span>
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void createProject(); }}
            placeholder="Projektname"
            className="mt-1 w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm"
          />
        </label>
      </GuardedModal>

      {loadError && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300 flex flex-wrap items-center justify-between gap-2" data-test-anchor="ladefehler">
          <span>{loadError}</span>
          <button type="button" onClick={() => void load()} className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs">Erneut laden</button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground text-center py-4 flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Lädt…
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground italic">
          Noch keine Projekte für diesen Kunden.
        </div>
      ) : (
        <div className="rounded-lg bg-slate-900/40 border border-slate-800 divide-y divide-slate-800">
          {items.map((p) => (
            <button
              key={p.id}
              onClick={() => navigate(`/m/bauberichteV2/projekte/${p.id}`)}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-800/40 text-left ${p.archivedAt ? 'opacity-60' : ''}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-foreground truncate">{p.name}</div>
                {p.parentName && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">↳ Sub von „{p.parentName}"</div>
                )}
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">{PROJECT_STATUS_LABEL[p.status]}</span>
              <span className="text-[10px] text-muted-foreground">{p.taskCount ?? 0} Aufg.</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
